import MetricDetailsDialog from "@Components/common/metrics/metric-details-dialog";
import { AppContext } from "@Context/common/general/context";
import AnalyticsEvents from "@Enums/AnalyticsEvents";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { useTrainingFocus } from "@hooks/game/progress/use-training-focus";
import useI18n from "@i18n";
import { Stack, Typography, useTheme } from "@mui/material";
import { useContext, useRef, useState } from "react";

import { TrainingFocusCard } from "@Components/game/progress/training-focus/training-focus-card";
import { TrainingFocusSnackbar } from "@Components/game/progress/training-focus/training-focus-snackbar";
import { useTrainingFocusTransition } from "@Components/game/progress/training-focus/use-training-focus-transition";

const TrainingFocusSection = () => {
  const { state } = useContext(AppContext);
  const { i18n } = useI18n();
  const { track } = useAnalytics();
  const theme = useTheme();
  const { trainingFocus, loading, hasLoadedTrainingFocus, recalculate } = useTrainingFocus();
  const [dialogOpen, setDialogOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const metric = trainingFocus?.metric ?? null;
  const { displayedMetric, previousMetric, isLoading, isTransitionActive, isTransitionEntered, prepareMetricTransition, completeMetricTransition } =
    useTrainingFocusTransition({
      metric,
      loading,
      hasLoadedTrainingFocus,
    });

  const handleNavigateToSection = () => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleClick = () => {
    if (!metric) {
      track(AnalyticsEvents.analyze_my_performance_clicked);
      prepareMetricTransition();
      recalculate().catch(() => undefined);
      return;
    }

    track(AnalyticsEvents.how_to_improve_clicked, {
      metric_focus: metric,
      user_rating: state.user.userRating,
    });
    setDialogOpen(true);
  };

  return (
    <>
      <TrainingFocusSnackbar
        open={hasLoadedTrainingFocus && trainingFocus === null}
        navBarHeight={state.app.navBarHeight}
        onNavigateToSection={handleNavigateToSection}
      />

      <Stack ref={sectionRef} gap={2} sx={{ scrollMarginTop: `calc(${state.app.navBarHeight}px + ${theme.spacing(2)})` }}>
        <Typography variant="textLg700" color="text.primary">
          {i18n("progress.training-focus.title")}
        </Typography>
        <TrainingFocusCard
          displayedMetric={displayedMetric}
          previousMetric={previousMetric}
          isLoading={isLoading}
          isTransitionActive={isTransitionActive}
          isTransitionEntered={isTransitionEntered}
          onClick={handleClick}
          onTransitionComplete={completeMetricTransition}
        />
        <MetricDetailsDialog isTrainingFocus metric={metric} open={dialogOpen} onClose={() => setDialogOpen(false)} />
      </Stack>
    </>
  );
};

export default TrainingFocusSection;
